import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Check, Search, Loader2, ArrowLeft, Trophy, Users, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/api';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';
import ViewerAuthModal from '@/components/common/ViewerAuthModal';
import { resolveMediaUrl } from '@/utils/helpers';
import { toast } from 'sonner';
import { SEO } from '@/components/common/SEO';

const PreferencesPage = () => {
  const { viewer } = useViewerAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamSearch, setTeamSearch] = useState('');

  // Selections
  const [selectedSports, setSelectedSports] = useState(new Set());
  const [selectedLeagues, setSelectedLeagues] = useState(new Set());
  const [selectedTeams, setSelectedTeams] = useState(new Set());

  const loadData = useCallback(async () => {
    try {
      const [sportsRes, leaguesRes, teamsRes] = await Promise.all([
        api.getSports(),
        api.getLeagues(),
        api.getTeams ? api.getTeams() : Promise.resolve({ data: [] }),
      ]);
      setSports(sportsRes.data || []);
      setLeagues(leaguesRes.data || []);
      setTeams(teamsRes.data || []);

      // Load existing preferences if viewer is logged in
      if (viewer) {
        try {
          const prefsRes = await api.getPreferences();
          const p = prefsRes.data;
          if (p.has_setup) {
            setSelectedSports(new Set(p.favorite_sports || []));
            setSelectedLeagues(new Set(p.favorite_leagues || []));
            setSelectedTeams(new Set(p.favorite_teams || []));
          }
        } catch { /* no prefs yet */ }
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  }, [viewer]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSport = (id) => {
    setSelectedSports(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleLeague = (id) => {
    setSelectedLeagues(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleTeam = (id) => {
    setSelectedTeams(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!viewer) {
      setShowAuth(true);
      return;
    }
    setSaving(true);
    try {
      await api.updatePreferences({
        favorite_sports: [...selectedSports],
        favorite_leagues: [...selectedLeagues],
        favorite_teams: [...selectedTeams],
      });
      toast.success('Preferences saved! Your Live TV feed is now personalized.');
    } catch (e) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  // Filter leagues by selected sports
  const filteredLeagues = selectedSports.size > 0
    ? leagues.filter(l => selectedSports.has(l.sport_id))
    : leagues;

  // Filter teams by search and selected sports/leagues
  const filteredTeams = teams.filter(t => {
    if (teamSearch && !t.name?.toLowerCase().includes(teamSearch.toLowerCase())) return false;
    if (selectedSports.size > 0 && t.sport_id && !selectedSports.has(t.sport_id)) return false;
    return true;
  });

  const totalSelections = selectedSports.size + selectedLeagues.size + selectedTeams.size;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950" data-testid="preferences-page">
      <SEO title="My Preferences | NLS" description="Set your favorite sports, teams, and leagues for a personalized viewing experience." />

      {/* Header */}
      <div className="bg-gradient-to-b from-sky-900/40 to-transparent pt-8 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white flex items-center gap-1 mb-4 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-white">My Preferences</h1>
          <p className="text-gray-400 mt-2 text-base">
            Tell us what you love watching. We'll personalize your Live TV channel with your favorite content.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16 -mt-4 space-y-8">

        {/* Step 1: Sports */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">Sports</h2>
            {selectedSports.size > 0 && (
              <Badge className="bg-sky-500/20 text-sky-300 text-xs">{selectedSports.size} selected</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="sports-grid">
            {sports.map(sport => {
              const selected = selectedSports.has(sport.id);
              return (
                <button
                  key={sport.id}
                  onClick={() => toggleSport(sport.id)}
                  data-testid={`sport-${sport.slug}`}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    selected
                      ? 'border-sky-400 bg-sky-500/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                  }`}
                >
                  {selected && (
                    <div className="absolute top-2 right-2">
                      <Check className="w-4 h-4 text-sky-400" />
                    </div>
                  )}
                  {sport.logo && (
                    <img src={resolveMediaUrl(sport.logo)} alt="" className="w-8 h-8 mb-2 object-contain" />
                  )}
                  <span className="text-white font-medium text-sm">{sport.name}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 2: Leagues */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Leagues</h2>
            {selectedLeagues.size > 0 && (
              <Badge className="bg-amber-500/20 text-amber-300 text-xs">{selectedLeagues.size} selected</Badge>
            )}
            {selectedSports.size > 0 && (
              <span className="text-gray-500 text-xs ml-auto">Filtered by your sports</span>
            )}
          </div>
          {filteredLeagues.length === 0 ? (
            <p className="text-gray-500 text-sm">Select a sport above to see its leagues.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="leagues-grid">
              {filteredLeagues.map(league => {
                const selected = selectedLeagues.has(league.id);
                return (
                  <button
                    key={league.id}
                    onClick={() => toggleLeague(league.id)}
                    data-testid={`league-${league.id}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      selected
                        ? 'border-amber-400 bg-amber-500/10'
                        : 'border-gray-700 bg-gray-800/40 hover:border-gray-600'
                    }`}
                  >
                    {league.logo && (
                      <img src={resolveMediaUrl(league.logo)} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                    )}
                    <span className="text-white text-sm truncate flex-1">{league.name}</span>
                    {selected && <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Step 3: Teams */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Teams</h2>
            {selectedTeams.size > 0 && (
              <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">{selectedTeams.size} selected</Badge>
            )}
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={teamSearch}
              onChange={e => setTeamSearch(e.target.value)}
              placeholder="Search teams..."
              className="bg-gray-800/60 border-gray-700 text-white pl-9"
              data-testid="team-search"
            />
          </div>
          {filteredTeams.length === 0 ? (
            <p className="text-gray-500 text-sm">
              {teamSearch ? 'No teams match your search.' : 'Select a sport above to see teams.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[320px] overflow-y-auto pr-1" data-testid="teams-grid">
              {filteredTeams.slice(0, 60).map(team => {
                const selected = selectedTeams.has(team.id);
                return (
                  <button
                    key={team.id}
                    onClick={() => toggleTeam(team.id)}
                    data-testid={`team-${team.id}`}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left ${
                      selected
                        ? 'border-emerald-400 bg-emerald-500/10'
                        : 'border-gray-700/50 bg-gray-800/30 hover:border-gray-600'
                    }`}
                  >
                    {team.logo ? (
                      <img src={resolveMediaUrl(team.logo)} alt="" className="w-6 h-6 object-contain flex-shrink-0 rounded" />
                    ) : (
                      <div className="w-6 h-6 rounded bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs font-bold">{team.name?.[0]}</span>
                      </div>
                    )}
                    <span className="text-white text-xs truncate flex-1">{team.name}</span>
                    {selected && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Save Button - Sticky */}
        <div className="sticky bottom-4 z-10">
          <Card className="bg-gray-900/95 backdrop-blur border-gray-700 shadow-2xl">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div className="text-gray-300 text-sm">
                {totalSelections === 0 ? (
                  <span className="text-gray-500">No preferences selected yet</span>
                ) : (
                  <span>
                    <strong className="text-white">{totalSelections}</strong> preference{totalSelections !== 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600 text-white px-6"
                data-testid="save-preferences"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Heart className="w-4 h-4 mr-1" />}
                {viewer ? 'Save Preferences' : 'Sign In to Save'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <ViewerAuthModal open={showAuth} onClose={() => { setShowAuth(false); loadData(); }} />
    </div>
  );
};

export default PreferencesPage;
